import { useContext, useEffect, useState } from 'react';

import { LayoutContext } from '@lcsc-ui/layouts/baseLayout/provider';
import useUserStore from '@lcsc-ui/store/user';
import { buriedPoint } from '@lcsc-ui/utils/buriedPoint';
import Domains from '@lcsc-ui/utils/domains';
import sendAxios from '@lcsc-ui/utils/request';
import Utils from '@lcsc-ui/utils/Utils';
import { Input, DatePicker, Modal, Form, message, InputNumber, Button } from 'antd';
import zhCN from 'antd/es/date-picker/locale/zh_CN';
import dayjs from 'dayjs';
import { styled } from 'styled-components';

import InputWrapper from '@/components/InputWrapper';
import useMounted from '@/hooks/useMounted';

const ZhLocale = zhCN;
ZhLocale.lang = {
  ...zhCN.lang,
  monthFormat: 'M月',
  shortWeekDays: ['日', '一', '二', '三', '四', '五', '六'],
};

const DatePickAndInput = styled.div`
  /* 屏幕宽度大于 1552 像素的样式 */
  @media only screen and (min-width: 1552px) {
    width: 254px;
    margin-right: ${(props) => props.lgmr}px;
    .ant-picker {
      width: 137px;
    }
    .ant-input {
      width: 95px;
    }
  }
  height: 42px;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  width: 232px;
  .ant-picker {
    width: 137px;
    border: none;
  }
  .ant-input {
    width: 117px;
    border: none;
  }
  > * {
    border: none;
    &.ant-picker {
      width: 137px;
      position: relative;
      &::after {
        content: '';
        position: absolute;
        right: 0;
        top: 8px;
        width: 1px;
        height: 24px;
        background: #dcdfe6;
      }
    }
    .ant-picker-outlined:focus-within {
      border: none;
    }
  }
`;

const QuickFindMaterialComponent = ({ isLg, backgroundColor = 'bg-gradient-to-b from-[#DCF2FF]' }) => {
  const { resourceData } = useContext(LayoutContext);
  const [isFmModalOpen, setIsFmModalOpen] = useState(false);
  const [fmSubmiting, setFmSubmiting] = useState(false);
  const [fmModalProps, setFmModalOpenProps] = useState(false);
  const [stockpileHistory, setStockpileHistory] = useState(null);
  const [findModelList, setFindModelList] = useState([]); // 输入的找料型号
  const isMounted = useMounted();

  // 备货相关状态
  const [stockModel, setStockModel] = useState(); // 备货型号
  const [planList, setPlanList] = useState([
    { date: '', pickNum: '' },
    { date: '', pickNum: '' },
  ]); // 备货计划

  useEffect(() => {
    getData();
  }, []);

  function getData() {
    sendAxios({
      method: 'get',
      url: '/www/stockpile/history',
      baseURL: Domains.lcscWwwUrl,
    }).then((res) => {
      if (res.code === 200 && res.result) {
        setStockpileHistory(res.result);
      }
    });
  }

  function handleFindModelListChange(idx, value) {
    const newFindModelList = [...findModelList];
    newFindModelList[idx] = value;
    setFindModelList(newFindModelList);
  }

  function toFindPage(event) {
    let url = `${resourceData?.projectConfigVO?.lcscOrderUrl}/stock/and/find.html`;
    if (findModelList.length) {
      url += `?models=${encodeURI(encodeURI(findModelList.filter(Boolean).join('|')))}`;
    }
    buriedPoint({
      pf: 'pc',
      ty: 'click',
      cp: 'index',
      op: 'WWW_GO_FIND_PAGE',
      data: {
        models: findModelList.filter(Boolean).join(),
        isCarryingParams: Boolean(findModelList.length),
        t: Date.now(), // 当前时间
      },
    });
    const finalUrl = Utils.getBtnSpmQueryUrl(url, event.target);
    window.open(finalUrl, '_blank');
  }

  // 备货相关函数
  function onDatePickerChange(idx, [date, dateString]) {
    setPlanList((pre) => {
      pre.splice(idx, 1, { ...pre[idx], date: date ? dateString || '' : '' });
      return pre;
    });
  }

  function disabledDate(current) {
    // 限制只能选择 14 天后至两年内的日期
    const minDate = dayjs().add(14, 'days');
    const maxDate = dayjs().add(2, 'years');
    // current 是 moment 对象，需要转换为 dayjs 对象进行比较
    const currentDay = dayjs(current);
    return currentDay.isBefore(minDate) || currentDay.isAfter(maxDate);
  }

  function onStockPickNumChange(idx, value) {
    setPlanList((pre) => {
      pre.splice(idx, 1, { ...pre[idx], pickNum: value });
      return pre;
    });
  }

  function toStockPage(event) {
    // 校验输入数量的合法性
    const pickNums = planList.map((t) => t.pickNum).filter(Boolean);
    if (pickNums.length) {
      const reg = /^(?!0)(?:[0-9]{1,8}|100000000)$/;
      if (!pickNums.some((t) => reg.test(t))) {
        message.warning('请填写正确数量！');
        return;
      }
    }

    let url = `${resourceData?.projectConfigVO?.lcscOrderUrl}/stock/and/find.html?type=stock`;
    if (stockModel) {
      url += `&stockModel=${encodeURI(encodeURI(stockModel))}`;
    }
    if (planList.some((t) => t.date || t.pickNum)) {
      // 只传带参数到URL
      url += `&planList=${encodeURIComponent(JSON.stringify(planList.filter((p) => p.date || p.pickNum)))}`;
    }
    buriedPoint({
      cp: 'index',
      pf: 'pc',
      ty: 'click',
      op: 'WWW_GO_STOCK_PAGE',
      data: {
        stockModel: stockModel,
        isCarryingParams: Boolean(stockModel || planList.some((t) => t.date || t.pickNum)),
        t: Date.now(), // 当前时间
      },
    });
    const finalUrl = Utils.getBtnSpmQueryUrl(url, event.target);
    window.open(finalUrl, '_blank');
  }

  const [fmForm] = Form.useForm();
  const userInfo = useUserStore((state) => state.userInfo);

  function showFmModal(item) {
    setIsFmModalOpen(true);
    setFmModalOpenProps(item);
    const { productModel, brandName } = item;
    fmForm.setFieldsValue({ productModel, brandName, expectNumber: null });
    // 快速找料入口埋点
    buriedPoint({
      pf: 'pc',
      cp: 'index',
      ty: 'click',
      op: 'WWW_GO_HOT_MATERIAL',
      data: {
        hotMaterial: item,
        isLogin: userInfo?.customerLogin === 1,
        t: Date.now(), // 当前时间戳,
      },
    });
  }

  const handleCancel = () => {
    setIsFmModalOpen(false);
  };

  const onFinish = (values) => {
    setFmSubmiting(true);
    sendAxios({
      method: 'post',
      url: `/async/enquiry/save`,
      baseURL: Domains.lcscOrderUrl,
      data: [values],
    })
      .then((res) => {
        if (res.needLogin) {
          Utils.showWindowLogin();
          message.warning('请登陆后操作！');
          return;
        }
        if (res.code === 200) {
          message.success('找料需求提交成功，我们的工作人员将尽快联系您。');
        }
      })
      .finally(() => {
        setIsFmModalOpen(false);
        setFmSubmiting(false);
      });
  };

  return (
    <div>
      <div
        className={`_lg:flex _lg:items-center _lg:w-[1171px] w-[883px] h-[112px] ${backgroundColor} rounded-[20px] pt-[20px] _lg:pt-[0] px-[20px]`}
      >
        <div className="flex _lg:flex-col items-baseline _lg:mr-[44px]">
          <p className="text-[18px] font-bold text-[#111] mr-[12px] leading-[24px] _lg:mb-[4px]">
            免费<span className="text-lcBlue">备货</span>
          </p>
          <p className="text-[12px] text-[#54657D] leading-[16px]">给个料号 即用即提</p>
        </div>
        <div className="mt-[16px] _lg:mt-0 flex justify-between">
          <InputWrapper lgmr={20}>
            <Input
              placeholder="输入型号"
              value={stockModel}
              className="w-[230px] h-[42px] _lg:mr-[20px] _lg:w-[254px]"
              onChange={(e) => setStockModel(e.target.value)}
              maxLength={32}
            />
          </InputWrapper>
          <DatePickAndInput lgmr={20}>
            {isMounted && (
              <DatePicker
                disabledDate={disabledDate}
                locale={ZhLocale}
                placeholder="首次提货日期"
                className="w-[137px] h-[38px]"
                onChange={(...meta) => onDatePickerChange(0, meta)}
                icon
              />
            )}
            <Input
              placeholder="提货数量"
              onChange={(e) => onStockPickNumChange(0, e.target.value)}
              className="w-[95px] h-[38px] _lg:w-[117px]"
              maxLength={32}
            />
          </DatePickAndInput>
          <DatePickAndInput lgmr={56}>
            {isMounted && (
              <DatePicker
                disabledDate={disabledDate}
                locale={ZhLocale}
                placeholder="二次提货日期"
                className="w-[137px] h-[38px]"
                onChange={(...meta) => onDatePickerChange(1, meta)}
              />
            )}
            <Input
              placeholder="提货数量"
              onChange={(e) => onStockPickNumChange(1, e.target.value)}
              className="w-[95px] h-[38px] _lg:w-[117px]"
              maxLength={32}
            />
          </DatePickAndInput>
          <button
            type="primary"
            className="w-[103px] h-[40px] hover:bg-[#0093E6] text-[#0093e6] bg-[#CFEEFF] border-[1px] border-solid border-[#0093E6] border-opacity-[40%] rounded-[20px] hover:text-[#fff] text-[16px] leading-[40px]"
            onClick={toStockPage}
            data-spm="bh"
          >
            我要备货
          </button>
        </div>
      </div>
      <div
        className={`_lg:w-[1171px] w-[883px] h-[421px] ${backgroundColor} rounded-[20px] pt-[20px] px-[20px] mt-[20px]`}
      >
        <div className="_lg:flex _lg:items-center">
          <div className="flex _lg:flex-col items-baseline _lg:mr-[44px] _lg:pt-[14px]">
            <p className="text-[18px] font-bold text-[#111] mr-[12px] leading-[24px] _lg:mb-[4px]">
              快速<span className="text-lcBlue">找料</span>
            </p>
            <p className="text-[12px] text-[#54657D] leading-[16px]">快速报价 全球找料</p>
          </div>
          <div className="mt-[16px] _lg:mt-[14px] flex justify-between">
            <InputWrapper lgmr={20}>
              <Input
                placeholder="输入型号1"
                onChange={(e) => handleFindModelListChange(0, e.target.value)}
                className="h-[42px]"
                maxLength={32}
              />
            </InputWrapper>
            <InputWrapper lgmr={20}>
              <Input
                placeholder="输入型号2"
                onChange={(e) => handleFindModelListChange(1, e.target.value)}
                className="h-[42px]"
                maxLength={32}
              />
            </InputWrapper>
            <InputWrapper lgmr={56}>
              <Input
                placeholder="输入型号3"
                onChange={(e) => handleFindModelListChange(2, e.target.value)}
                className="h-[42px]"
                maxLength={32}
              />
            </InputWrapper>
            <button
              type="primary"
              className="w-[103px] h-[40px] hover:bg-[#0093e6] bg-[#CFEEFF] border-[1px] border-solid border-[#0093E6] border-opacity-[40%] rounded-[20px] hover:text-[#fff] text-[#0093E6] text-[16px] leading-[40px]"
              onClick={toFindPage}
              data-spm="zl"
            >
              我要找料
            </button>
          </div>
        </div>
        <div className="flex items-baseline mt-[16px] _lg:mt-[30px]">
          <p className="text-[18px] font-bold text-[#111] mr-[12px] leading-[24px]">
            热门<span className="text-lcBlue">找料</span>
          </p>
          <p className="text-[12px] text-[#54657D] leading-[16px]">一键询价 热门找料</p>
        </div>
        {stockpileHistory && (
          <div className="h-[264px] mt-[16px]">
            <ul className="flex flex-col flex-wrap h-full">
              {stockpileHistory.hotMaterial.slice(0, isLg ? 32 : 24).map((item, idx) => {
                return (
                  <li
                    key={idx}
                    className={`${(idx + 1) % 8 === 0 ? 'mb-0' : 'mb-[14px]'} cursor-pointer pl-[32px] text-[14px] leading-[19px] flex items-center relative w-[250px] hover:text-lcBlue`}
                    onClick={() => showFmModal(item)}
                  >
                    {item?.isHot && (
                      <img
                        className="absolute left-0 top-0"
                        src="https://static.szlcsc.com/ecp/assets/newWeb/www/home/hot-icon.svg"
                      />
                    )}
                    <span title={item?.productModel + ' ' + item?.brandName} className={`truncate antialiased`}>
                      {item?.productModel} {item?.brandName}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {fmModalProps && (
        <Modal title="热门找料" centered open={isFmModalOpen} footer={null} onCancel={handleCancel}>
          <Form
            form={fmForm}
            name="basic"
            labelCol={{
              span: 8,
            }}
            wrapperCol={{
              span: 16,
            }}
            style={{
              maxWidth: 600,
            }}
            initialValues={{
              remember: true,
            }}
            onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item
              label="型号"
              name="productModel"
              rules={[
                {
                  required: true,
                  message: '请输入型号!',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item label="品牌" name="brandName">
              <Input />
            </Form.Item>
            <Form.Item
              label="数量"
              name="expectNumber"
              rules={[
                {
                  required: true,
                  pattern: /^(?!0)(?:[0-9]{1,8}|100000000)$/,
                  message: '请输入正确的数量!',
                },
              ]}
            >
              <InputNumber className="w-full" min={1} max={99999999} controls={false} />
            </Form.Item>
            <Form.Item label="">
              <div className="flex justify-end">
                <Button type="primary" htmlType="submit" loading={fmSubmiting}>
                  确定提交
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default QuickFindMaterialComponent;
